import { useState, type ReactElement } from 'react';

import { Alert, Button, Empty, Form, Input, Modal, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { useNotes, useCreateNoteMutation } from '../../notes/hooks/useNotes.js';
import { useMembers } from '../../tasks/hooks/useTaskLookups.js';
import { formatDateTime } from '../../../shared/format/index.js';
import { i18n } from '../../../i18n/index.js';

interface ProjectNotesTabProps {
  projectId: string;
}

interface NoteCreateValues {
  body: string;
}

export function ProjectNotesTab({ projectId }: ProjectNotesTabProps): ReactElement {
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm<NoteCreateValues>();
  const { data, isLoading } = useNotes({ projectId, limit: 100 });
  const createMut = useCreateNoteMutation();
  const { data: members } = useMembers();
  const rows = data ?? [];
  const memberName = (memberId: string | undefined): string => {
    const member = (members ?? []).find((entry) => entry.id === memberId);
    return member ? member.name || member.email : '—';
  };

  const submit = async (): Promise<void> => {
    const values = await form.validateFields();
    await createMut.mutateAsync({
      body: values.body.trim(),
      links: [{ projectId }],
    });
    form.resetFields();
    setCreateOpen(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-medium text-[var(--ant-color-text-secondary)]">
          {i18n.t('crm.features.features.projects.components.projectnotestab.заметки_проекта', {
            p0: rows.length,
          })}
        </span>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
          data-testid="project-notes-create"
        >
          {i18n.t('crm.features.features.projects.components.projectnotestab.новая_заметка')}
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={i18n.t(
            'crm.features.features.projects.components.projectnotestab.заметок_нет',
          )}
        />
      ) : (
        <div className="flex flex-col divide-y divide-[var(--ant-color-border-secondary)] rounded-lg border border-[var(--ant-color-border)] bg-white">
          {rows.map((note) => (
            <div key={note.id} className="px-4 py-3">
              <p className="m-0 whitespace-pre-wrap break-words text-[13px] text-[var(--ant-color-text)]">
                {note.body}
              </p>
              <p className="mt-1 mb-0 text-xs text-[var(--ant-color-text-tertiary)]">
                {memberName(note.createdByMember?.id)} · {formatDateTime(note.createdAt)}
              </p>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => void submit()}
        okText={i18n.t('crm.features.features.projects.components.projectnotestab.создать')}
        cancelText={i18n.t('crm.common.cancel')}
        confirmLoading={createMut.isPending}
        destroyOnHidden
        title={i18n.t('crm.features.features.projects.components.projectnotestab.новая_заметка')}
      >
        {createMut.isError ? (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            title={(createMut.error as Error).message}
          />
        ) : null}
        <Form form={form} layout="vertical" onFinish={() => void submit()}>
          <Form.Item
            name="body"
            label={i18n.t('crm.features.features.projects.components.projectnotestab.текст')}
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={4} maxLength={10_000} data-testid="project-note-body" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
